import styles from "./AssignContractPopup.module.scss";
import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Form, FormInstance } from "antd";
import FormItemSelect from "../ui/FormItemSelect/FormItemSelect";
import CustomButton from "../ui/CustomButton/CustomButton";
import {
  useGetContractForAssignQuery,
  useGetGymListingForAssignContractSelectQuery,
} from "../../api/queries";
import { useContractMutations } from "../../api/mutations";
import { openSuccessMessage } from "../../store/message/messageSlice";
import { handleError } from "../../helper/errorHandler";
import useAppDispatch from "../../hooks/useAppDispatch";

interface IAssignContractPopup {
  gymListingId?: string;
  contractId?: string;
  contractVersionId?: string;
  closeFn: () => void;
  getData?: () => void;
}

const AssignContractPopup: React.FC<IAssignContractPopup> = ({
  gymListingId,
  closeFn,
  contractId,
  contractVersionId,
  getData,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const formRef = useRef<FormInstance>(null);

  const [selectedContractId, setSelectedContractId] = useState<string | null>(null);

  const { data: gymListing = [] } = useGetGymListingForAssignContractSelectQuery();
  const { data: contractsForAssign = [] } = useGetContractForAssignQuery();

  const { assignContract } = useContractMutations();

  const contracts = useMemo(() => {
    if (!contractsForAssign) return [];
    return contractsForAssign.map((el) => {
      return {
        id: el.id,
        label: el.title,
      };
    });
  }, [contractsForAssign]);

  const contractVersions = useMemo(() => {
    if (contractId) {
      const contract = contractsForAssign.find((el) => el.id === contractId);
      if (!contract) return [];
      return contract.contractVersions.map((el) => {
        return {
          id: el.id,
          label: el.version,
        };
      });
    }
    if (!selectedContractId) return [];
    const contract = contractsForAssign.find((el) => el.id === selectedContractId);
    if (!contract) return [];
    return contract.contractVersions.map((el) => {
      return {
        id: el.id,
        label: el.version,
      };
    });
  }, [selectedContractId, contractsForAssign]);

  const onFinish = (values: {
    gymListingId: string;
    contractId: string;
    contractVersionId: string;
  }) => {
    const model = {
      gymListingId: values.gymListingId,
      contractVersionId: values.contractVersionId,
    };

    assignContract(model, {
      onSuccess: () => {
        dispatch(openSuccessMessage(t("ContractAssignedSuccessfully")));
        closeFn();
        if (getData) {
          getData();
        }
      },
      onError: (e) => handleError(e, dispatch, t),
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.popup}>
        <h3 className={styles.title}>{t("AssignContract")}</h3>
        <Form
          ref={formRef}
          className={styles.form}
          name="assign-contract"
          onFinish={onFinish}
          autoComplete={"off"}
          layout={"vertical"}
          initialValues={{
            gymListingId: gymListingId,
            contractId: contractId,
            contractVersionId: contractVersionId,
          }}
        >
          <FormItemSelect
            name={t("gymListingId")}
            label={t("SelectGym")}
            rules={{ required: true }}
            items={gymListing}
            showSearch={true}
            disabled={!!gymListingId}
          />
          <FormItemSelect
            name={t("contractId")}
            label={t("SelectContract")}
            rules={{ required: true }}
            items={contracts}
            onChange={(val) => {
              setSelectedContractId(val.toString());
              formRef.current?.setFieldsValue({
                contractVersionId: null,
              });
            }}
            disabled={!!contractId}
          />
          <FormItemSelect
            name={t("contractVersionId")}
            label={t("SelectVersion")}
            rules={{ required: true }}
            items={contractVersions}
            disabled={!!contractVersionId}
          />
          <div className={styles.btnsWrapper}>
            <CustomButton
              text={t("Close")}
              type={"button"}
              typeStyle={"outline"}
              functionOnClick={closeFn}
            />
            <CustomButton text={t("Assign")} type={"submit"} typeStyle={"fill"} />
          </div>
        </Form>
      </div>
    </div>
  );
};

export default AssignContractPopup;
